//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

import { MAX_UPLOAD_BYTES, MAX_PDF_PAGES, MAX_PDF_BYTES, formatMb } from '@/utils/rfi-import-files'

// Client-side PDF page chooser for the AI RFI import (LSO-717). Mirrors RfiSheetSelect:
// reads the page count in the browser (detecting here rather than a server round trip
// keeps the import a single upload) and lets the user pick a subset. The
// server rebuilds the PDF from just these pages before sending it to the AI. Emits:
//   - confirm(pages): 1-based page numbers to read. An empty array means "no choice
//     needed" (≤1 page, or the file couldn't be parsed) — the server reads the whole
//     document, preserving the original behavior.
//   - cancel: the user backed out.
export default {
  name: 'RfiPageSelect',
  props: {
    // The uploaded File (native browser File object).
    file: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      pageCount: 0,
      selected: [],
      rangeText: '',
      rangeValid: true
    }
  },
  computed: {
    allSelected() {
      return this.pageCount > 0 && this.selected.length === this.pageCount
    },
    fileBytes() {
      return (this.file && this.file.size) || 0
    },
    // A selection the server is certain to refuse: too many pages, or the whole file
    // (nothing trimmed) over the size limit.
    blockReason() {
      if (this.selected.length > MAX_PDF_PAGES) {
        return `${this.selected.length} pages are selected; the A.I. can read at most ${MAX_PDF_PAGES} per import. Select ${MAX_PDF_PAGES} or fewer.`
      }
      if (this.allSelected && this.fileBytes > MAX_PDF_BYTES) {
        return `This PDF is ${formatMb(this.fileBytes)}; the A.I. can read at most ${formatMb(MAX_PDF_BYTES)} at once. Select fewer pages.`
      }
      return ''
    },
    // A partial selection of a large file: the trimmed PDF's size can't be known here,
    // so estimate it pro rata and warn rather than block.
    sizeWarning() {
      if (!this.pageCount || this.fileBytes <= MAX_PDF_BYTES) { return '' }
      const estimate = this.fileBytes * this.selected.length / this.pageCount
      return estimate > MAX_PDF_BYTES
        ? `This PDF is ${formatMb(this.fileBytes)}, and the selected pages may still be over the ${formatMb(MAX_PDF_BYTES)} the A.I. can read at once. If the import fails, select fewer pages.`
        : ''
    }
  },
  watch: {
    // Keep the range text in sync when the checkboxes change, so the two controls
    // always agree without letting a text edit and a checkbox toggle fight.
    selected() {
      this.rangeText = this.formatRange(this.selected)
      this.rangeValid = true
    }
  },
  mounted() {
    this.read()
  },
  methods: {
    async read() {
      this.loading = true
      try {
        // No file (caller bug / stale state): back out rather than silently
        // confirming an empty selection.
        if (!this.file) {
          this.$emit('cancel')
          return
        }
        // Never parse an oversized file on the main thread; the caller already refuses
        // these, so this only guards a direct use of the picker.
        if (this.file.size > MAX_UPLOAD_BYTES) {
          this.$emit('cancel')
          return
        }
        // Loaded on demand: the parser is large and only this step needs it, so it must
        // not ride along with every page that can open the import.
        const { PDFDocument } = await import('pdf-lib')
        const buffer = await this.readArrayBuffer(this.file)
        const pdf = await PDFDocument.load(new Uint8Array(buffer), { ignoreEncryption: true })
        const count = pdf.getPageCount()
        // Nothing to choose — let the server read the whole document as before. A
        // one-page file over the size limit still stops here so the user is told now,
        // not by a failed job later.
        if (count <= 1 && this.file.size <= MAX_PDF_BYTES) {
          this.$emit('confirm', [])
          return
        }
        this.pageCount = count
        this.selected = Array.from({ length: count }, (_, i) => i + 1)
      } catch (error) {
        // A client-side parse failure must not block the import — fall back to the
        // server reading the whole document.
        this.$emit('confirm', [])
      } finally {
        this.loading = false
      }
    },
    readArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = () => reject(reader.error || new Error('Could not read the file'))
        reader.readAsArrayBuffer(file)
      })
    },
    // Parses "3-8, 12" into a sorted, deduped, in-range list of page numbers. Returns
    // null when the text has a token that isn't a page/range within 1..pageCount.
    parseRange(text) {
      const tokens = String(text || '').split(',').map(t => t.trim()).filter(Boolean)
      const out = new Set()
      for (const token of tokens) {
        const range = token.match(/^(\d+)\s*-\s*(\d+)$/)
        const single = token.match(/^(\d+)$/)
        if (range) {
          const a = parseInt(range[1], 10)
          const b = parseInt(range[2], 10)
          if (a < 1 || b < 1 || a > this.pageCount || b > this.pageCount || a > b) { return null }
          for (let n = a; n <= b; n++) { out.add(n) }
        } else if (single) {
          const n = parseInt(single[1], 10)
          if (n < 1 || n > this.pageCount) { return null }
          out.add(n)
        } else {
          return null
        }
      }
      return [...out].sort((x, y) => x - y)
    },
    // Collapses a sorted page list into compact range text, e.g. [1,2,3,5] -> "1-3, 5".
    formatRange(pages) {
      const sorted = [...pages].sort((a, b) => a - b)
      const parts = []
      let start = null
      let prev = null
      const flush = () => {
        if (start === null) { return }
        parts.push(start === prev ? `${start}` : `${start}-${prev}`)
      }
      for (const n of sorted) {
        if (prev !== null && n === prev + 1) { prev = n; continue }
        flush()
        start = n
        prev = n
      }
      flush()
      return parts.join(', ')
    },
    applyRange() {
      const parsed = this.parseRange(this.rangeText)
      if (parsed === null) {
        this.rangeValid = false
        return
      }
      this.rangeValid = true
      this.selected = parsed
    },
    toggleAll() {
      this.selected = this.allSelected
        ? []
        : Array.from({ length: this.pageCount }, (_, i) => i + 1)
    },
    confirm() {
      this.$emit('confirm', [...this.selected].sort((a, b) => a - b))
    }
  }
}
